import { safeSessionStorage } from '@toss/storage';

import {
  getExternalPlatformDebugOverride,
  getGoogleClassroomDebugContext,
} from '../externalPlatform/debug';
import { GoogleOAuthStorage } from '../google/GoogleOAuthStorage';

import { GOOGLE_CLASSROOM_LANDING_PAGE_TYPE_PARAM_KEY } from './constants';
import {
  type GoogleClassroomPageContext,
  LandingPageType,
  UserType,
} from './types';

const GOOGLE_SIGN_IN_SCOPE = 'openid profile email';

export class GoogleClassroomContextManager {
  private _sessionStorageKey = '@gc/context';
  // 컨텍스트가 (bridge effect 로) sessionStorage 에 늦게 써질 때 구독자에게 알린다.
  // useIsInGoogleClassroom 이 useSyncExternalStore 로 이걸 구독해, 최초 렌더 이후
  // 컨텍스트가 생겨도 다시 평가되게 한다(안 그러면 memo 가 stale-false 로 고정).
  private _listeners = new Set<() => void>();

  public initializeLandingPageContext(
    searchParams = new URLSearchParams(location.search),
  ) {
    const contextFromSearchParams =
      this.getLandingPageContextFromSearchParams(searchParams);
    safeSessionStorage.set(
      this._sessionStorageKey,
      JSON.stringify(contextFromSearchParams),
    );
    this._notify();

    return contextFromSearchParams;
  }

  /** useSyncExternalStore 구독. 컨텍스트 persist 시 리스너를 깨운다. */
  public subscribe = (listener: () => void): (() => void) => {
    this._listeners.add(listener);
    return () => {
      this._listeners.delete(listener);
    };
  };

  /** useSyncExternalStore 스냅샷. sessionStorage 원본 문자열(불변이면 동일 primitive). */
  public getContextSnapshot = (): string | null =>
    safeSessionStorage.get(this._sessionStorageKey);

  private _notify() {
    this._listeners.forEach(listener => listener());
  }

  /**
   * 현재 URL 의 search params 로 컨텍스트를 즉시 도출한다(sessionStorage 미persist 상태).
   * bridge 가 sessionStorage 로 persist 하기 전 '첫 프레임'에도 GC 진입을 감지해,
   * CRM 등 게이트가 한 프레임도 활성화되지 않게 한다. 비-GC URL 은 userType=Unknown
   * 을 돌려주고, 필수 파라미터 누락(부분 GC URL)은 throw 하므로 안전하게 null 로 감싼다.
   */
  public getLandingPageContextFromCurrentUrl(): GoogleClassroomPageContext | null {
    if (typeof window === 'undefined') return null;
    try {
      return this.getLandingPageContextFromSearchParams(
        new URLSearchParams(window.location.search),
      );
    } catch {
      return null;
    }
  }

  /**
   * location이 변경되어도 상태가 유지되도록 sessionStorage를 이용해서 Context를 관리합니다.
   * 탭이나 브라우저를 닫으면 상태가 날아갑니다.
   */
  public getLandingPageContext(): GoogleClassroomPageContext | null {
    const debugOverride = getExternalPlatformDebugOverride();
    if (debugOverride) {
      return debugOverride.mode === 'googleClassroom'
        ? getGoogleClassroomDebugContext()
        : null;
    }

    const context = safeSessionStorage.get(this._sessionStorageKey);
    return context == null ? null : JSON.parse(context);
  }

  private _getUserType(pageType: LandingPageType): UserType {
    switch (pageType) {
      case LandingPageType.AttachmentSetup:
      case LandingPageType.TeacherView:
      case LandingPageType.StudentWorkReview:
        return UserType.Teacher;
      case LandingPageType.StudentView:
        return UserType.Student;
      case LandingPageType.OutOfClassroom:
      default:
        return UserType.Unknown;
    }
  }

  public getGoogleOAuthScope(): string {
    const getClassroomScopes = () => {
      const context = this.getLandingPageContext();
      if (context == null) {
        return [];
      }

      switch (context.userType) {
        case UserType.Teacher:
          return ['https://www.googleapis.com/auth/classroom.addons.teacher'];
        case UserType.Student:
          return ['https://www.googleapis.com/auth/classroom.addons.student'];
        case UserType.Unknown:
        default:
          return [];
      }
    };

    return [GOOGLE_SIGN_IN_SCOPE, ...getClassroomScopes()].join(' ');
  }

  public getGoogleOAuthToken(): string | null {
    return GoogleOAuthStorage.getToken();
  }

  public getLandingPageContextFromSearchParams(
    params = new URLSearchParams(window.location.search),
  ): GoogleClassroomPageContext {
    const courseId = params.get('courseId');
    const itemId = params.get('itemId');
    const itemType = params.get('itemType');
    const pageType = params.get(
      GOOGLE_CLASSROOM_LANDING_PAGE_TYPE_PARAM_KEY,
    ) as LandingPageType;
    const userType = this._getUserType(pageType);

    switch (pageType) {
      case LandingPageType.AttachmentSetup: {
        const addOnToken = params.get('addOnToken');

        if (!courseId || !itemId || !itemType || !addOnToken) {
          throw new MissingRequiredParametersError();
        }

        return {
          pageType,
          userType,
          params: {
            courseId,
            itemId,
            itemType,
            addOnToken,
          },
        };
      }
      case LandingPageType.TeacherView: {
        const attachmentId = params.get('attachmentId');
        if (!courseId || !itemId || !itemType || !attachmentId) {
          throw new MissingRequiredParametersError();
        }

        return {
          pageType,
          userType,
          params: {
            courseId,
            itemId,
            itemType,
            attachmentId,
          },
        };
      }
      case LandingPageType.StudentView: {
        const attachmentId = params.get('attachmentId');
        if (!courseId || !itemId || !itemType || !attachmentId) {
          throw new MissingRequiredParametersError();
        }

        return {
          pageType,
          userType,
          params: {
            courseId,
            itemId,
            itemType,
            attachmentId,
          },
        };
      }
      case LandingPageType.StudentWorkReview: {
        const attachmentId = params.get('attachmentId');
        const submissionId = params.get('submissionId');

        if (
          !courseId ||
          !itemId ||
          !itemType ||
          !attachmentId ||
          !submissionId
        ) {
          throw new MissingRequiredParametersError();
        }

        return {
          pageType,
          userType,
          params: {
            courseId,
            itemId,
            itemType,
            attachmentId,
            submissionId,
          },
        };
      }
      case LandingPageType.OutOfClassroom:
      default:
        return {
          pageType,
          userType,
          params: {},
        };
    }
  }
}

export const googleClassroomContextManager =
  new GoogleClassroomContextManager();

export class MissingRequiredParametersError extends Error {
  constructor(message: string = 'Missing required parameters.') {
    super(message);
    this.name = 'MissingRequiredParametersError';
  }
}
